import { useState, useEffect } from 'react';
import { getProfiles, getProfile, type Profile } from '../api/client';
import { format } from 'date-fns';

function RiskLevelBadge({ level }: { level?: string }) {
  const classes: Record<string, string> = {
    low: 'bg-green-500/20 text-green-400',
    medium: 'bg-yellow-500/20 text-yellow-400',
    high: 'bg-orange-500/20 text-orange-400',
    critical: 'bg-red-500/20 text-red-400',
  };
  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${classes[level || 'low'] || 'bg-gray-500/20 text-gray-400'}`}>
      {level?.toUpperCase() || 'UNKNOWN'}
    </span>
  );
}

function SignalCount({ count, label, color = 'blue' }: { count: number; label: string; color?: 'blue' | 'red' | 'orange' | 'purple' }) {
  const colorClasses = {
    blue: 'bg-blue-500/20 text-blue-400',
    red: 'bg-red-500/20 text-red-400',
    orange: 'bg-orange-500/20 text-orange-400',
    purple: 'bg-purple-500/20 text-purple-400',
  };
  
  return (
    <div className={`flex items-center justify-between p-2 rounded ${colorClasses[color]}`}>
      <span className="text-sm">{label}</span>
      <span className="font-bold">{count}</span>
    </div>
  );
}

export default function ProfilesPage() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [showModal, setShowModal] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    risk_level: '',
    min_score: '',
    max_score: '',
    limit: 50,
    offset: 0,
  });

  useEffect(() => {
    loadProfiles();
  }, [filters]);

  async function loadProfiles() {
    setLoading(true);
    setError(null);

    const result = await getProfiles({
      risk_level: filters.risk_level || undefined,
      min_score: filters.min_score ? parseInt(filters.min_score) : undefined,
      max_score: filters.max_score ? parseInt(filters.max_score) : undefined,
      limit: filters.limit,
      offset: filters.offset,
    });

    if (result.error) {
      setError(result.error);
    } else if (result.data) {
      setProfiles(result.data.profiles);
      setTotal(result.data.total);
    }

    setLoading(false);
  }

  async function handleViewProfile(profile: Profile) {
    // Try to get fresh data, fall back to list data
    const result = await getProfile(profile.fp_visitor_id);
    if (result.data) {
      setSelectedProfile(result.data.profile);
    } else {
      setSelectedProfile(profile);
    }
    setShowModal(true);
  }

  function handleFilterChange(key: keyof typeof filters, value: string | number) {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      offset: 0, // Reset to first page on filter change
    }));
  }

  const pageCount = Math.ceil(total / filters.limit);
  const currentPage = Math.floor(filters.offset / filters.limit) + 1;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Attacker Profiles</h1>

      {/* Description */}
      <div className="card mb-6">
        <p className="text-sm text-gray-400">
          Profiles aggregate visitor behavior over time, keyed by Fingerprint visitor ID. 
          Track suspect scores, signal patterns, and velocity across sessions.
        </p>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Risk Level</label>
            <select
              className="select"
              value={filters.risk_level}
              onChange={(e) => handleFilterChange('risk_level', e.target.value)}
            >
              <option value="">All</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Min Avg Score</label>
            <input
              type="number"
              className="input"
              placeholder="0"
              value={filters.min_score}
              onChange={(e) => handleFilterChange('min_score', e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">Max Avg Score</label>
            <input
              type="number"
              className="input"
              placeholder="100"
              value={filters.max_score}
              onChange={(e) => handleFilterChange('max_score', e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : error ? (
        <div className="bg-red-500/20 text-red-400 p-4 rounded-lg">
          Error loading profiles: {error}
        </div>
      ) : (
        <>
          <div className="text-sm text-gray-400 mb-2">
            Showing {profiles.length} of {total} profiles
          </div>

          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Visitor ID</th>
                  <th>Risk Level</th>
                  <th>Avg Score (24h)</th>
                  <th>Max Score (7d)</th>
                  <th>Events</th>
                  <th>First Seen</th>
                  <th>Last Seen</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {profiles.map((profile) => (
                  <tr key={profile.profile_id}>
                    <td className="font-mono text-xs">
                      {profile.fp_visitor_id.substring(0, 16)}...
                    </td>
                    <td>
                      <RiskLevelBadge level={profile.current_risk_level || 'low'} />
                    </td>
                    <td>
                      <span className={`font-bold ${
                        (profile.rolling_suspect_score_avg_24h || 0) >= 50 ? 'text-red-400' :
                        (profile.rolling_suspect_score_avg_24h || 0) >= 30 ? 'text-yellow-400' : 'text-green-400'
                      }`}>
                        {profile.rolling_suspect_score_avg_24h?.toFixed(1) || '-'}
                      </span>
                    </td>
                    <td>
                      <span className={`font-bold ${
                        (profile.max_suspect_score_7d || 0) >= 70 ? 'text-red-400' :
                        (profile.max_suspect_score_7d || 0) >= 50 ? 'text-orange-400' : 'text-green-400'
                      }`}>
                        {profile.max_suspect_score_7d || '-'}
                      </span>
                    </td>
                    <td>{profile.event_count_total}</td>
                    <td className="text-sm">
                      {format(new Date(profile.first_seen_at), 'MMM d, yyyy')}
                    </td>
                    <td className="text-sm">
                      {format(new Date(profile.last_seen_at), 'MMM d, HH:mm')}
                    </td>
                    <td>
                      <button
                        onClick={() => handleViewProfile(profile)}
                        className="text-blue-400 hover:text-blue-300"
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-gray-400">
              Page {currentPage} of {pageCount}
            </div>
            <div className="flex gap-2">
              <button
                className="btn btn-secondary"
                disabled={filters.offset === 0}
                onClick={() => setFilters((prev) => ({ ...prev, offset: prev.offset - prev.limit }))}
              >
                Previous
              </button>
              <button
                className="btn btn-secondary"
                disabled={filters.offset + profiles.length >= total}
                onClick={() => setFilters((prev) => ({ ...prev, offset: prev.offset + prev.limit }))}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      {/* Profile detail modal */}
      {showModal && selectedProfile && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-auto m-4">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold">Profile Details</h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white"
              >
                ✕
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Header Card */}
              <div className="card bg-gradient-to-r from-gray-800 to-gray-900">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="text-sm text-gray-400 mb-1">Visitor ID</div>
                    <div className="font-mono text-sm">{selectedProfile.fp_visitor_id}</div>
                  </div>
                  <RiskLevelBadge level={selectedProfile.current_risk_level || 'low'} />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-900 p-3 rounded-lg text-center">
                    <div className="text-sm text-gray-400 mb-1">Avg Score (24h)</div>
                    <div className={`text-2xl font-bold ${
                      (selectedProfile.rolling_suspect_score_avg_24h || 0) >= 50 ? 'text-red-400' :
                      (selectedProfile.rolling_suspect_score_avg_24h || 0) >= 30 ? 'text-yellow-400' : 'text-green-400'
                    }`}>
                      {selectedProfile.rolling_suspect_score_avg_24h?.toFixed(1) || '-'}
                    </div>
                  </div>
                  <div className="bg-gray-900 p-3 rounded-lg text-center">
                    <div className="text-sm text-gray-400 mb-1">Max Score (7d)</div>
                    <div className={`text-2xl font-bold ${
                      (selectedProfile.max_suspect_score_7d || 0) >= 70 ? 'text-red-400' :
                      (selectedProfile.max_suspect_score_7d || 0) >= 50 ? 'text-orange-400' : 'text-green-400'
                    }`}>
                      {selectedProfile.max_suspect_score_7d || '-'}
                    </div>
                  </div>
                  <div className="bg-gray-900 p-3 rounded-lg text-center">
                    <div className="text-sm text-gray-400 mb-1">Total Events</div>
                    <div className="text-2xl font-bold text-blue-400">
                      {selectedProfile.event_count_total}
                    </div>
                  </div>
                </div>
              </div>

              {/* Velocity Metrics */}
              <div className="card">
                <h3 className="font-semibold mb-3">Velocity (24h)</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <div className="text-sm text-gray-400 mb-1">Events</div>
                    <div className="text-xl font-mono">{selectedProfile.velocity_24h_events}</div>
                  </div>
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <div className="text-sm text-gray-400 mb-1">Distinct IPs</div>
                    <div className="text-xl font-mono">{selectedProfile.velocity_24h_distinct_ips}</div>
                  </div>
                  <div className="bg-gray-900 p-3 rounded-lg">
                    <div className="text-sm text-gray-400 mb-1">Distinct Countries</div>
                    <div className="text-xl font-mono">{selectedProfile.velocity_24h_distinct_countries}</div>
                  </div>
                </div>
              </div>

              {/* Signal Summary (7d) */}
              <div className="card">
                <h3 className="font-semibold mb-3">Signal Hits (7d)</h3>
                <div className="grid grid-cols-4 gap-3">
                  <SignalCount count={selectedProfile.bot_bad_hits_7d} label="Bot" color="red" />
                  <SignalCount count={selectedProfile.vpn_hits_7d} label="VPN" color="orange" />
                  <SignalCount count={selectedProfile.proxy_residential_hits_7d} label="Proxy" color="purple" />
                  <SignalCount count={selectedProfile.tampering_hits_7d} label="Tampering" color="red" />
                  <SignalCount count={selectedProfile.devtools_hits_7d} label="DevTools" color="blue" />
                  <SignalCount count={selectedProfile.high_activity_device_hits_7d} label="High Activity" color="orange" />
                  <SignalCount count={selectedProfile.tor_hits_7d} label="Tor" color="red" />
                </div>
              </div>

              {/* Linked Entities */}
              {((selectedProfile.ips_seen?.length || 0) + (selectedProfile.asns_seen?.length || 0) + (selectedProfile.countries_seen?.length || 0)) > 0 && (
                <div className="card">
                  <h3 className="font-semibold mb-3">Linked Entities</h3>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <div className="text-gray-400 mb-2">IPs Seen</div>
                      <div className="space-y-1">
                        {selectedProfile.ips_seen?.slice(0, 5).map((ip, i) => (
                          <div key={i} className="font-mono text-xs bg-gray-900 px-2 py-1 rounded">{ip.substring(0, 20)}...</div>
                        ))}
                        {(selectedProfile.ips_seen?.length || 0) > 5 && (
                          <div className="text-gray-500 text-xs">+{selectedProfile.ips_seen!.length - 5} more</div>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-400 mb-2">ASNs Seen</div>
                      <div className="space-y-1">
                        {selectedProfile.asns_seen?.slice(0, 5).map((asn, i) => (
                          <div key={i} className="font-mono text-xs bg-gray-900 px-2 py-1 rounded">{asn}</div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-gray-400 mb-2">Countries</div>
                      <div className="space-y-1">
                        {selectedProfile.countries_seen?.slice(0, 5).map((country, i) => (
                          <div key={i} className="text-xs bg-gray-900 px-2 py-1 rounded">{country}</div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Labels */}
              {selectedProfile.labels && selectedProfile.labels.length > 0 && (
                <div className="card">
                  <h3 className="font-semibold mb-3">Labels</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedProfile.labels.map((label, i) => (
                      <span key={i} className="bg-blue-500/20 text-blue-400 px-2 py-1 rounded text-sm">
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="card">
                <h3 className="font-semibold mb-3">Timeline</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">First Seen</span>
                    <span>{format(new Date(selectedProfile.first_seen_at), 'PPpp')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Last Seen</span>
                    <span>{format(new Date(selectedProfile.last_seen_at), 'PPpp')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Profile Created</span>
                    <span>{format(new Date(selectedProfile.created_at), 'PPpp')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Last Updated</span>
                    <span>{format(new Date(selectedProfile.updated_at), 'PPpp')}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {selectedProfile.notes && selectedProfile.notes.length > 0 && (
                <div className="card">
                  <h3 className="font-semibold mb-3">Analyst Notes</h3>
                  <div className="space-y-3">
                    {selectedProfile.notes.map((note, i) => (
                      <div key={i} className="bg-gray-900 p-3 rounded">
                        <div className="flex justify-between text-xs text-gray-400 mb-1">
                          <span>{note.by}</span>
                          <span>{format(new Date(note.ts), 'PPp')}</span>
                        </div>
                        <div className="text-sm">{note.text}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
